'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  LayoutGrid,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  Pencil,
  GripVertical,
  Settings2,
} from 'lucide-react';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { InstitutionMultiSelect } from '@/components/institution/InstitutionMultiSelect';
import { Switch } from '@/components/ui/Switch';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from '@/components/ui';
import { api } from '@/lib/api';
import { mergeExploreChartDatasetIfMissing } from '@/lib/institution-bi-catalog-fallback';
import { cn } from '@/lib/utils';

const DEFAULT_CHART_COLORS = ['#0C1C38', '#457b9d', '#e9c46a', '#e76f51', '#8338ec', '#06d6a0'];

/** Dernier tableau BI sélectionné (préférence navigateur, partagée sur le domaine). */
const INSTITUTION_BI_LAST_DASHBOARD_COOKIE = 'lyrra_inst_bi_last_dashboard_id';
const INSTITUTION_BI_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 400;

function readInstitutionBiLastDashboardCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = `; ${document.cookie}`.split(`; ${INSTITUTION_BI_LAST_DASHBOARD_COOKIE}=`);
  if (parts.length < 2) return null;
  const raw = parts.pop()?.split(';').shift();
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function persistInstitutionBiLastDashboardCookie(id: string | null): void {
  if (typeof document === 'undefined') return;
  const attrs = 'Path=/; SameSite=Lax';
  if (!id?.trim()) {
    document.cookie = `${INSTITUTION_BI_LAST_DASHBOARD_COOKIE}=; ${attrs}; Max-Age=0`;
    return;
  }
  document.cookie = `${INSTITUTION_BI_LAST_DASHBOARD_COOKIE}=${encodeURIComponent(id.trim())}; ${attrs}; Max-Age=${INSTITUTION_BI_COOKIE_MAX_AGE_SEC}`;
}

function unwrapData<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

type ExploreSchemaSource = {
  key: string;
  labelKey: string;
  dimensions: { key: string; labelKey: string }[];
  numericColumns: { key: string; labelKey: string }[];
};

type BiConfigHints = {
  supportsLimit?: boolean;
  limitDefault?: number;
  limitMax?: number;
  kpiKeys?: { key: string; labelKey: string }[];
  tableColumns?: { key: string; labelKey: string }[];
  barMetricKeys?: { key: string; labelKey: string }[];
  supportsColors?: boolean;
  supportsGridSpan?: boolean;
  exploreSchema?: ExploreSchemaSource[];
};

type ExploreMeasureForm = { agg: 'count' | 'sum' | 'avg'; column?: string; seriesKey: string };

function suggestExploreSeriesKey(agg: ExploreMeasureForm['agg'], column?: string): string {
  if (agg === 'count') return 'count';
  const base = column ? `${agg}_${column}` : agg;
  const safe = base.replace(/[^a-zA-Z0-9_]/g, '_');
  const trimmed = safe.slice(0, 40);
  return /^[a-zA-Z]/.test(trimmed) ? trimmed : `m_${trimmed}`.slice(0, 40);
}

function ExploreChartFormFields({
  t,
  exploreSchema,
  limitMax,
  source,
  setSource,
  dimension,
  setDimension,
  measures,
  setMeasures,
  limitValue,
  setLimitValue,
  widgetType,
}: {
  t: (k: string) => string;
  exploreSchema: ExploreSchemaSource[];
  limitMax: number;
  source: string;
  setSource: (v: string) => void;
  dimension: string;
  setDimension: (v: string) => void;
  measures: ExploreMeasureForm[];
  setMeasures: React.Dispatch<React.SetStateAction<ExploreMeasureForm[]>>;
  limitValue: string;
  setLimitValue: (v: string) => void;
  widgetType: string;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-[#E8EDF5] bg-[#F8FAFC]/80 p-3 dark:border-[#B6CAEC] dark:bg-[#122644]/40">
      <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.explore.maxMeasures')}</p>
      <InstitutionStyledSelect
        panelZIndexClass="z-[200]"
        label={t('institution.bi.explore.dataSource')}
        options={exploreSchema.map((s) => ({ value: s.key, label: t(s.labelKey) }))}
        value={source}
        onChange={(src) => {
          setSource(src);
          const sch = exploreSchema.find((s) => s.key === src);
          if (sch?.dimensions[0]) setDimension(sch.dimensions[0].key);
          setMeasures([{ agg: 'count', seriesKey: 'count' }]);
        }}
      />
      {(() => {
        const srcDef = exploreSchema.find((s) => s.key === source) ?? exploreSchema[0];
        if (!srcDef) return null;
        return (
          <>
            <InstitutionStyledSelect
              panelZIndexClass="z-[200]"
              label={t('institution.bi.explore.dimension')}
              options={srcDef.dimensions.map((d) => ({
                value: d.key,
                label: t(d.labelKey) !== d.labelKey ? t(d.labelKey) : d.key,
              }))}
              value={dimension}
              onChange={setDimension}
            />
            <fieldset>
              <legend className="text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                {t('institution.bi.explore.measures')}
              </legend>
              <div className="mt-2 space-y-2">
                {measures.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col gap-2 rounded-md border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#0C1C38]"
                  >
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[120px] flex-1">
                        <InstitutionStyledSelect
                          panelZIndexClass="z-[200]"
                          label={t('institution.bi.explore.measureAgg')}
                          className="[&_button]:px-2 [&_button]:py-1 [&_button]:text-sm"
                          options={[
                            { value: 'count', label: t('institution.bi.explore.agg.count') },
                            {
                              value: 'sum',
                              label: t('institution.bi.explore.agg.sum'),
                              disabled: !srcDef.numericColumns.length,
                            },
                            {
                              value: 'avg',
                              label: t('institution.bi.explore.agg.avg'),
                              disabled: !srcDef.numericColumns.length,
                            },
                          ]}
                          value={row.agg}
                          onChange={(v) => {
                            const agg = v as ExploreMeasureForm['agg'];
                            const firstNum = srcDef.numericColumns[0]?.key;
                            setMeasures((prev) =>
                              prev.map((m, i) =>
                                i === idx
                                  ? {
                                      agg,
                                      column: agg === 'count' ? undefined : firstNum,
                                      seriesKey: suggestExploreSeriesKey(agg, agg === 'count' ? undefined : firstNum),
                                    }
                                  : m,
                              ),
                            );
                          }}
                        />
                      </div>
                      {row.agg !== 'count' && (
                        <div className="min-w-[140px] flex-1">
                          <InstitutionStyledSelect
                            panelZIndexClass="z-[200]"
                            label={t('institution.bi.explore.columnForAgg')}
                            className="[&_button]:px-2 [&_button]:py-1 [&_button]:text-sm"
                            options={srcDef.numericColumns.map((nc) => ({
                              value: nc.key,
                              label: t(nc.labelKey) !== nc.labelKey ? t(nc.labelKey) : nc.key,
                            }))}
                            value={row.column ?? ''}
                            onChange={(col) => {
                              setMeasures((prev) =>
                                prev.map((m, i) =>
                                  i === idx
                                    ? {
                                        ...m,
                                        column: col,
                                        seriesKey: suggestExploreSeriesKey(m.agg, col),
                                      }
                                    : m,
                                ),
                              );
                            }}
                          />
                        </div>
                      )}
                      <label className="min-w-[100px] flex-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                        {t('institution.bi.explore.seriesKey')}
                        <input
                          className="mt-0.5 w-full rounded border border-[#E8EDF5] px-2 py-1 font-mono text-xs dark:border-[#B6CAEC] dark:bg-[#122644]"
                          value={row.seriesKey}
                          onChange={(e) =>
                            setMeasures((prev) =>
                              prev.map((m, i) => (i === idx ? { ...m, seriesKey: e.target.value } : m)),
                            )
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline dark:text-red-400"
                        onClick={() => setMeasures((prev) => prev.filter((_, i) => i !== idx))}
                        disabled={measures.length <= 1}
                      >
                        {t('institution.bi.explore.removeMeasure')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="mt-2 text-sm text-[color:var(--inst-player-primary,#0C1C38)] hover:underline"
                onClick={() =>
                  setMeasures((prev) =>
                    prev.length >= 4 ? prev : [...prev, { agg: 'count', seriesKey: `m${prev.length + 1}` }],
                  )
                }
                disabled={measures.length >= 4}
              >
                {t('institution.bi.explore.addMeasure')}
              </button>
            </fieldset>
            <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
              {t('institution.bi.limit')}
              <input
                type="number"
                min={1}
                max={limitMax}
                className="mt-1 w-full rounded-lg border border-[#E8EDF5] px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                value={limitValue}
                onChange={(e) => setLimitValue(e.target.value)}
              />
            </label>
            {widgetType === 'pie' && (
              <p className="text-xs text-amber-800 dark:text-amber-200">{t('institution.bi.explore.pieNote')}</p>
            )}
          </>
        );
      })()}
    </div>
  );
}

type BiDatasetMeta = {
  key: string;
  labelKey: string;
  descriptionKey: string;
  allowedTypes: string[];
  configHints?: BiConfigHints;
};

type CatalogPayload = { datasets: BiDatasetMeta[] };

type DashboardRow = {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  groupIds: string[];
  categoryIds: string[];
  groups?: { id: string; name: string }[];
  categories?: { id: string; name: string }[];
  widgets?: unknown[];
};

type BiGroupOption = { id: string; name: string };
type BiCategoryOption = { id: string; name: string };

type WidgetDataRow = Record<string, string | number | boolean | null>;

type WidgetWithData = {
  id: string;
  title: string;
  widgetType: string;
  datasetKey: string;
  config: Record<string, unknown>;
  sortOrder: number;
  gridSpan: number;
  rows: WidgetDataRow[];
  error: string | null;
};

type DashboardDataPayload = {
  dashboard: {
    id: string;
    title: string;
    description: string | null;
    sortOrder: number;
    groupIds?: string[];
    categoryIds?: string[];
  };
  widgets: WidgetWithData[];
};

function readConfig(w: WidgetWithData): Record<string, unknown> {
  const c = w.config;
  if (c && typeof c === 'object' && !Array.isArray(c)) return c as Record<string, unknown>;
  return {};
}

function colorsFromConfig(cfg: Record<string, unknown>): string[] {
  const raw = cfg.seriesColors;
  if (Array.isArray(raw)) {
    const hex = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;
    return raw.filter((c): c is string => typeof c === 'string' && hex.test(c));
  }
  return [];
}

function BiWidgetChart({
  widget,
  dateLocale,
  t,
  fillHeight = false,
}: {
  widget: WidgetWithData;
  dateLocale: typeof fr | undefined;
  t: (k: string) => string;
  /** Aperçu plein écran : le conteneur du graphique occupe la hauteur disponible (parent flex). */
  fillHeight?: boolean;
}) {
  const cfg = readConfig(widget);
  const rows = widget.rows;
  const palette = colorsFromConfig(cfg);
  const colors = palette.length ? palette : DEFAULT_CHART_COLORS;

  if (widget.error) {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-300" role="alert">
        {widget.error}
      </p>
    );
  }

  if (!rows.length) {
    return <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noData')}</p>;
  }

  const h = 260;
  const chartOuterClass = fillHeight ? 'h-full min-h-[280px] w-full min-w-0' : 'w-full min-w-0';
  const chartOuterStyle = fillHeight ? undefined : { height: h };

  if (widget.widgetType === 'kpi') {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r, i) => (
          <div
            key={`${r.metricKey ?? i}`}
            className="rounded-lg border border-[#F0F4FA] bg-[#F8FAFC]/80 px-3 py-2 dark:border-[#19335E] dark:bg-[#122644]/50"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]">
              {String(r.label ?? r.metricKey ?? '')}
            </p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-[#0C1C38] dark:text-white">
              {r.value == null ? '—' : typeof r.value === 'number' ? r.value.toLocaleString() : String(r.value)}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (widget.widgetType === 'pie') {
    const data = rows
      .map((r) => ({
        name: String(r.label ?? r.name ?? ''),
        value: typeof r.value === 'number' ? r.value : Number(r.value) || 0,
      }))
      .filter((d) => d.value > 0);
    if (!data.length) {
      return <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noNonZeroPie')}</p>;
    }
    return (
      <div style={chartOuterStyle} className={chartOuterClass}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} label>
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (widget.widgetType === 'bar') {
    const xKey = String(cfg.xKey ?? 'name');
    const yKeysBar =
      Array.isArray(cfg.yKeys) && cfg.yKeys.length > 0
        ? (cfg.yKeys as unknown[]).filter((k): k is string => typeof k === 'string')
        : [String(cfg.yKey ?? 'value')];
    const chartRows = rows.map((r) => {
      const o: Record<string, unknown> = { ...r };
      if (xKey === 'weekStart' && typeof r.weekStart === 'string' && dateLocale) {
        try {
          o._xLabel = format(parseISO(`${r.weekStart}T12:00:00.000Z`), 'd MMM', { locale: dateLocale });
        } catch {
          o._xLabel = r.weekStart;
        }
      } else {
        o._xLabel = r[xKey];
      }
      return o;
    });
    const showLegend = yKeysBar.length > 1;
    return (
      <div style={chartOuterStyle} className={chartOuterClass}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-[#E8EDF5] dark:stroke-[#19335E]" />
            <XAxis dataKey="_xLabel" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} />
            <Tooltip />
            {showLegend ? <Legend /> : null}
            {yKeysBar.map((yk, i) => (
              <Bar
                key={yk}
                dataKey={yk}
                fill={colors[i % colors.length] ?? DEFAULT_CHART_COLORS[i % DEFAULT_CHART_COLORS.length]}
                radius={[4, 4, 0, 0]}
                name={yk}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (widget.widgetType === 'line') {
    const xKey = String(cfg.xKey ?? 'weekStart');
    const yKeys = Array.isArray(cfg.yKeys)
      ? (cfg.yKeys as unknown[]).filter((k): k is string => typeof k === 'string')
      : ['activeEnrollments', 'completions'];
    const chartRows = rows.map((r) => {
      const o: Record<string, unknown> = { ...r };
      if (xKey === 'weekStart' && typeof r.weekStart === 'string' && dateLocale) {
        try {
          o._label = format(parseISO(`${r.weekStart}T12:00:00.000Z`), 'd MMM', { locale: dateLocale });
        } catch {
          o._label = r.weekStart;
        }
      } else {
        o._label = r[xKey];
      }
      return o;
    });
    return (
      <div style={{ height: h }} className="w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-[#E8EDF5] dark:stroke-[#19335E]" />
            <XAxis dataKey="_label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} />
            <Tooltip />
            <Legend />
            {yKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (widget.widgetType === 'table') {
    const keys = Object.keys(rows[0] ?? {});
    if (!keys.length) return null;
    return (
      <div
        className={cn(
          'overflow-auto rounded-lg border border-[#E8EDF5] dark:border-[#19335E]',
          fillHeight ? 'max-h-full min-h-[200px] flex-1' : 'max-h-72',
        )}
      >
        <table className="min-w-full text-start text-xs">
          <thead className="sticky top-0 bg-[#F8FAFC] dark:bg-[#122644]">
            <tr>
              {keys.map((k) => (
                <th key={k} className="whitespace-nowrap px-2 py-2 font-medium text-[#5B6472] dark:text-[#DCE7F7]">
                  {t(`institution.bi.column.${k}`) !== `institution.bi.column.${k}` ? t(`institution.bi.column.${k}`) : k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-[#F0F4FA] dark:border-[#19335E]">
                {keys.map((k) => (
                  <td key={k} className="px-2 py-1.5 text-[#0C1C38] dark:text-[#DCE7F7]">
                    {r[k] == null ? '—' : String(r[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

function SortableWidgetCard({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: (drag: { attributes: Record<string, unknown>; listeners: Record<string, unknown> }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : undefined,
  };
  return (
    <article
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-xl border border-[#E8EDF5] bg-white p-4 shadow-sm dark:border-[#19335E] dark:bg-[#0C1C38]/50',
        className,
      )}
    >
      {children({ attributes, listeners })}
    </article>
  );
}

export default function InstitutionBiPanel() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? fr : undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogPayload | null>(null);
  const [dashboards, setDashboards] = useState<DashboardRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardDataPayload | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newDashTitle, setNewDashTitle] = useState('');
  const [newDashDesc, setNewDashDesc] = useState('');
  const [newDashGroupIds, setNewDashGroupIds] = useState<string[]>([]);
  const [newDashCategoryIds, setNewDashCategoryIds] = useState<string[]>([]);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editGroupIds, setEditGroupIds] = useState<string[]>([]);
  const [editCategoryIds, setEditCategoryIds] = useState<string[]>([]);
  const [biGroups, setBiGroups] = useState<BiGroupOption[]>([]);
  const [biCategories, setBiCategories] = useState<BiCategoryOption[]>([]);
  const [filterGroupIds, setFilterGroupIds] = useState<string[]>([]);
  const [filterCategoryIds, setFilterCategoryIds] = useState<string[]>([]);

  const [widgetDrawerOpen, setWidgetDrawerOpen] = useState(false);
  const [wDataset, setWDataset] = useState<string>('');
  const [wType, setWType] = useState<string>('');
  const [wTitle, setWTitle] = useState('');
  const [wGridSpan, setWGridSpan] = useState(1);

  const [configOpen, setConfigOpen] = useState(false);
  const [configWidget, setConfigWidget] = useState<WidgetWithData | null>(null);
  const [cfgLimit, setCfgLimit] = useState('');
  const [cfgKpis, setCfgKpis] = useState<string[]>([]);
  const [cfgCols, setCfgCols] = useState<string[]>([]);
  const [cfgColors, setCfgColors] = useState('');
  const [cfgYKey, setCfgYKey] = useState('');
  const [cfgGridSpan, setCfgGridSpan] = useState(1);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [wExploreSource, setWExploreSource] = useState('enrollments_detail');
  const [wDimension, setWDimension] = useState('flowTitle');
  const [wMeasures, setWMeasures] = useState<ExploreMeasureForm[]>([{ agg: 'count', seriesKey: 'count' }]);
  const [wExploreLimit, setWExploreLimit] = useState('20');
  const [cfgExploreSource, setCfgExploreSource] = useState('enrollments_detail');
  const [cfgDimension, setCfgDimension] = useState('flowTitle');
  const [cfgMeasures, setCfgMeasures] = useState<ExploreMeasureForm[]>([{ agg: 'count', seriesKey: 'count' }]);

  const [explorePreviewRows, setExplorePreviewRows] = useState<WidgetDataRow[]>([]);
  const [explorePreviewLoading, setExplorePreviewLoading] = useState(false);
  const [explorePreviewError, setExplorePreviewError] = useState<string | null>(null);
  const explorePreviewAbortRef = useRef<AbortController | null>(null);
  const explorePreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadCatalogAndList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawCat, rawList, rawGroups, rawCategories] = await Promise.all([
        api.get<unknown>('/institution/analytics/bi/catalog'),
        api.get<unknown>('/institution/analytics/bi/dashboards'),
        api.get<unknown>('/institution/groups'),
        api.get<unknown>('/institution/categories'),
      ]);
      const rawPayload = unwrapData<CatalogPayload>(rawCat);
      if (!rawPayload || !Array.isArray(rawPayload.datasets)) {
        setCatalog(null);
      } else {
        setCatalog({ datasets: mergeExploreChartDatasetIfMissing(rawPayload.datasets) });
      }
      const grpPayload = unwrapData<{ data?: BiGroupOption[] }>(rawGroups);
      const groupRows = Array.isArray(grpPayload?.data) ? grpPayload!.data! : [];
      setBiGroups(groupRows.map((g) => ({ id: g.id, name: g.name })));
      const catPayload = unwrapData<{ categories?: BiCategoryOption[] }>(rawCategories);
      const catRows = Array.isArray(catPayload?.categories) ? catPayload!.categories! : [];
      setBiCategories(catRows.map((c) => ({ id: c.id, name: c.name })));

      const list = unwrapData<DashboardRow[]>(rawList);
      const normalized = (Array.isArray(list) ? list : []).map((d) => ({
        ...d,
        groupIds: Array.isArray(d.groupIds) ? d.groupIds : [],
        categoryIds: Array.isArray(d.categoryIds) ? d.categoryIds : [],
      }));
      setDashboards(normalized);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.loadError');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadDashboardData = useCallback(
    async (id: string) => {
      setDataLoading(true);
      setError(null);
      try {
        const raw = await api.get<unknown>(`/institution/analytics/bi/dashboards/${id}/data`);
        setDashboardData(unwrapData<DashboardDataPayload>(raw));
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
            ? (e as { message: string }).message
            : t('institution.bi.dataError');
        setError(msg);
        setDashboardData(null);
      } finally {
        setDataLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadCatalogAndList();
  }, [loadCatalogAndList]);

  const dashboardsSorted = useMemo(
    () =>
      [...dashboards].sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
      ),
    [dashboards],
  );

  const dashboardsFiltered = useMemo(() => {
    let list = dashboardsSorted;
    if (filterGroupIds.length > 0) {
      list = list.filter((d) => {
        const g = d.groupIds ?? [];
        if (g.length === 0) return true;
        return g.some((id) => filterGroupIds.includes(id));
      });
    }
    if (filterCategoryIds.length > 0) {
      list = list.filter((d) => {
        const c = d.categoryIds ?? [];
        if (c.length === 0) return false;
        return c.some((id) => filterCategoryIds.includes(id));
      });
    }
    return list;
  }, [dashboardsSorted, filterGroupIds, filterCategoryIds]);

  useLayoutEffect(() => {
    if (loading) return;
    if (dashboardsFiltered.length === 0) {
      setSelectedId((prev) => (prev !== null ? null : prev));
      return;
    }
    const ids = new Set(dashboardsFiltered.map((d) => d.id));
    setSelectedId((prev) => {
      if (prev && ids.has(prev)) return prev;
      const fromCookie = readInstitutionBiLastDashboardCookie();
      if (fromCookie && ids.has(fromCookie)) return fromCookie;
      return dashboardsFiltered[0]!.id;
    });
  }, [loading, dashboardsFiltered]);

  useEffect(() => {
    if (selectedId) persistInstitutionBiLastDashboardCookie(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) void loadDashboardData(selectedId);
    else setDashboardData(null);
  }, [selectedId, loadDashboardData]);

  const dashboardSelectValue =
    dashboardsFiltered.length === 0
      ? ''
      : selectedId && dashboardsFiltered.some((d) => d.id === selectedId)
        ? selectedId
        : dashboardsFiltered[0]!.id;

  const selectedDatasetMeta = useMemo(
    () => catalog?.datasets.find((d) => d.key === wDataset),
    [catalog, wDataset],
  );

  const configHints = useMemo(
    () => catalog?.datasets.find((d) => d.key === configWidget?.datasetKey)?.configHints,
    [catalog, configWidget?.datasetKey],
  );

  const exploreAddQueryConfig = useMemo(() => {
    if (wDataset !== 'explore_chart' || !widgetDrawerOpen) return null;
    const measures = wMeasures
      .filter((m) => m.seriesKey.trim())
      .slice(0, 4)
      .map((m) =>
        m.agg === 'count'
          ? { agg: 'count' as const, seriesKey: m.seriesKey.trim() }
          : { agg: m.agg, column: m.column, seriesKey: m.seriesKey.trim() },
      );
    if (!measures.length) return null;
    return {
      exploreSource: wExploreSource,
      dimension: wDimension,
      measures,
      limit: Number.parseInt(wExploreLimit, 10) || 20,
    };
  }, [widgetDrawerOpen, wDataset, wExploreSource, wDimension, wMeasures, wExploreLimit]);

  const exploreAddPreviewWidget = useMemo((): WidgetWithData | null => {
    if (!exploreAddQueryConfig) return null;
    const yKeys = exploreAddQueryConfig.measures.map((m) => m.seriesKey);
    return {
      id: '__preview__',
      title: wTitle.trim() || t('institution.bi.addWidget'),
      widgetType: wType,
      datasetKey: 'explore_chart',
      config: { xKey: 'name', yKeys, yKey: yKeys[0] ?? 'count' },
      sortOrder: 0,
      gridSpan: 1,
      rows: explorePreviewRows,
      error: explorePreviewError,
    };
  }, [exploreAddQueryConfig, wTitle, wType, explorePreviewRows, explorePreviewError, t]);

  useEffect(() => {
    if (!widgetDrawerOpen || wDataset !== 'explore_chart' || !exploreAddQueryConfig) {
      explorePreviewAbortRef.current?.abort();
      if (explorePreviewDebounceRef.current) clearTimeout(explorePreviewDebounceRef.current);
      setExplorePreviewRows([]);
      setExplorePreviewLoading(false);
      setExplorePreviewError(null);
      return;
    }

    if (explorePreviewDebounceRef.current) clearTimeout(explorePreviewDebounceRef.current);

    explorePreviewDebounceRef.current = setTimeout(() => {
      explorePreviewAbortRef.current?.abort();
      const ac = new AbortController();
      explorePreviewAbortRef.current = ac;
      setExplorePreviewLoading(true);
      setExplorePreviewError(null);

      void (async () => {
        try {
          const raw = await api.post<unknown>(
            '/institution/analytics/bi/query',
            {
              datasetKey: 'explore_chart',
              widgetType: wType,
              config: exploreAddQueryConfig,
            },
            { signal: ac.signal },
          );
          if (ac.signal.aborted) return;
          const payload = unwrapData<{ rows?: WidgetDataRow[] }>(raw);
          setExplorePreviewRows(Array.isArray(payload?.rows) ? payload.rows : []);
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          const msg =
            e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
              ? (e as { message: string }).message
              : t('institution.bi.dataError');
          setExplorePreviewError(msg);
          setExplorePreviewRows([]);
        } finally {
          if (!ac.signal.aborted) setExplorePreviewLoading(false);
        }
      })();
    }, 300);

    return () => {
      explorePreviewAbortRef.current?.abort();
      if (explorePreviewDebounceRef.current) clearTimeout(explorePreviewDebounceRef.current);
    };
  }, [widgetDrawerOpen, wDataset, wType, exploreAddQueryConfig, t]);

  useEffect(() => {
    if (!widgetDrawerOpen || wDataset !== 'explore_chart') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWidgetDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [widgetDrawerOpen, wDataset]);

  const openAddWidget = () => {
    const first = catalog?.datasets[0];
    const fk = first?.key ?? '';
    setWDataset(fk);
    const ft = first?.allowedTypes[0] ?? '';
    setWType(ft);
    setWTitle(first ? t(first.labelKey) : '');
    setWGridSpan(1);
    if (fk === 'explore_chart' && first?.configHints?.exploreSchema?.[0]) {
      const s0 = first.configHints.exploreSchema[0];
      setWExploreSource(s0.key);
      setWDimension(s0.dimensions[0]?.key ?? 'name');
      setWMeasures([{ agg: 'count', seriesKey: 'count' }]);
      setWExploreLimit(String(first.configHints.limitDefault ?? 20));
    }
    setWidgetDrawerOpen(true);
  };

  useEffect(() => {
    if (!widgetDrawerOpen || !selectedDatasetMeta) return;
    if (!selectedDatasetMeta.allowedTypes.includes(wType)) {
      setWType(selectedDatasetMeta.allowedTypes[0] ?? '');
    }
    setWTitle((prev) => (prev.trim() ? prev : t(selectedDatasetMeta.labelKey)));
  }, [widgetDrawerOpen, selectedDatasetMeta, wType, t]);

  const openConfig = (w: WidgetWithData) => {
    setConfigWidget(w);
    const c = readConfig(w);
    const hints = catalog?.datasets.find((d) => d.key === w.datasetKey)?.configHints;
    setCfgLimit(String(c.limit ?? hints?.limitDefault ?? ''));
    const kpis = Array.isArray(c.kpiKeys) ? c.kpiKeys.filter((x): x is string => typeof x === 'string') : [];
    setCfgKpis(kpis);
    const cols = Array.isArray(c.tableColumns) ? c.tableColumns.filter((x): x is string => typeof x === 'string') : [];
    setCfgCols(cols);
    const sc = Array.isArray(c.seriesColors) ? c.seriesColors.filter((x): x is string => typeof x === 'string') : [];
    setCfgColors(sc.join('\n'));
    setCfgYKey(typeof c.yKey === 'string' ? c.yKey : 'enrollments');
    setCfgGridSpan(w.gridSpan >= 2 ? 2 : 1);
    setCfgExploreSource(typeof c.exploreSource === 'string' ? c.exploreSource : 'enrollments_detail');
    setCfgDimension(typeof c.dimension === 'string' ? c.dimension : 'flowTitle');
    const rawM = Array.isArray(c.measures) ? c.measures : [];
    const parsed: ExploreMeasureForm[] = rawM
      .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x))
      .map((x) => ({
        agg: x.agg === 'sum' || x.agg === 'avg' || x.agg === 'count' ? x.agg : 'count',
        column: typeof x.column === 'string' ? x.column : undefined,
        seriesKey: typeof x.seriesKey === 'string' ? x.seriesKey : 'count',
      }));
    setCfgMeasures(parsed.length ? parsed : [{ agg: 'count', seriesKey: 'count' }]);
    setConfigOpen(true);
  };

  const saveWidgetConfig = async () => {
    if (!configWidget || !selectedId) return;
    setCfgSaving(true);
    try {
      const nextConfig: Record<string, unknown> = { ...readConfig(configWidget) };
      const hints = catalog?.datasets.find((d) => d.key === configWidget.datasetKey)?.configHints;
      if (hints?.supportsLimit && cfgLimit.trim()) {
        nextConfig.limit = Number.parseInt(cfgLimit, 10);
      }
      if (hints?.kpiKeys?.length) {
        const keys = cfgKpis.length ? cfgKpis : hints.kpiKeys.map((k) => k.key);
        nextConfig.kpiKeys = keys;
      }
      if (hints?.tableColumns?.length) {
        const cols = cfgCols.length ? cfgCols : hints.tableColumns.map((c) => c.key);
        nextConfig.tableColumns = cols;
      }
      if (hints?.supportsColors && cfgColors.trim()) {
        nextConfig.seriesColors = cfgColors
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (
        hints?.barMetricKeys?.length &&
        configWidget.widgetType === 'bar' &&
        (configWidget.datasetKey === 'top_flows' || configWidget.datasetKey === 'flows_overview')
      ) {
        nextConfig.yKey = cfgYKey;
      }
      if (configWidget.datasetKey === 'explore_chart' && hints?.exploreSchema?.length) {
        nextConfig.exploreSource = cfgExploreSource;
        nextConfig.dimension = cfgDimension;
        nextConfig.measures = cfgMeasures
          .filter((m) => m.seriesKey.trim())
          .slice(0, 4)
          .map((m) =>
            m.agg === 'count'
              ? { agg: 'count' as const, seriesKey: m.seriesKey.trim() }
              : { agg: m.agg, column: m.column, seriesKey: m.seriesKey.trim() },
          );
      }
      await api.patch(`/institution/analytics/bi/widgets/${configWidget.id}`, {
        config: nextConfig,
        gridSpan: cfgGridSpan,
      });
      setConfigOpen(false);
      setConfigWidget(null);
      void loadDashboardData(selectedId);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.renameError');
      setError(msg);
    } finally {
      setCfgSaving(false);
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    if (!selectedId || !dashboardData) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = dashboardData.widgets.map((w) => w.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const nextOrder = arrayMove(dashboardData.widgets, oldIndex, newIndex);
    setDashboardData({ ...dashboardData, widgets: nextOrder });
    try {
      await api.post(`/institution/analytics/bi/dashboards/${selectedId}/widgets/reorder`, {
        orderedWidgetIds: nextOrder.map((w) => w.id),
      });
    } catch {
      setError(t('institution.bi.reorderError'));
      void loadDashboardData(selectedId);
    }
  };

  const createDashboard = async () => {
    const title = newDashTitle.trim() || t('institution.bi.defaultDashboardTitle');
    const description = newDashDesc.trim() || null;
    try {
      const raw = await api.post<unknown>('/institution/analytics/bi/dashboards', {
        title,
        description,
        groupIds: newDashGroupIds,
        categoryIds: newDashCategoryIds,
      });
      const dash = unwrapData<DashboardRow>(raw);
      const normalized: DashboardRow = {
        ...dash,
        groupIds: Array.isArray(dash.groupIds) ? dash.groupIds : [],
        categoryIds: Array.isArray(dash.categoryIds) ? dash.categoryIds : [],
      };
      setDashboards((prev) => [...prev, normalized]);
      setSelectedId(normalized.id);
      setNewDashTitle('');
      setNewDashDesc('');
      setNewDashGroupIds([]);
      setNewDashCategoryIds([]);
      setAddOpen(false);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.createError');
      setError(msg);
    }
  };

  const deleteDashboard = async (id: string) => {
    try {
      await api.delete(`/institution/analytics/bi/dashboards/${id}`);
      setDashboards((prev) => prev.filter((d) => d.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success(t('institution.bi.toast.dashboardRemoved'));
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.deleteError');
      setError(msg);
    }
  };

  const saveRename = async () => {
    if (!selectedId) return;
    const title = renameTitle.trim();
    if (!title) return;
    const description = editDesc.trim() || null;
    try {
      await api.patch(`/institution/analytics/bi/dashboards/${selectedId}`, {
        title,
        description,
        groupIds: editGroupIds,
        categoryIds: editCategoryIds,
      });
      setDashboards((prev) =>
        prev.map((d) =>
          d.id === selectedId
            ? { ...d, title, description, groupIds: [...editGroupIds], categoryIds: [...editCategoryIds] }
            : d,
        ),
      );
      setDashboardData((prev) =>
        prev && prev.dashboard.id === selectedId
          ? {
              ...prev,
              dashboard: {
                ...prev.dashboard,
                title,
                description,
                groupIds: [...editGroupIds],
                categoryIds: [...editCategoryIds],
              },
            }
          : prev,
      );
      setRenameOpen(false);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.renameError');
      setError(msg);
    }
  };

  const addWidget = async () => {
    if (!selectedId || !wDataset || !wType) return;
    try {
      const body: Record<string, unknown> = {
        datasetKey: wDataset,
        widgetType: wType,
        title: wTitle.trim() || (selectedDatasetMeta ? t(selectedDatasetMeta.labelKey) : wDataset),
        gridSpan: wGridSpan,
      };
      if (wDataset === 'explore_chart') {
        body.config = {
          exploreSource: wExploreSource,
          dimension: wDimension,
          measures: wMeasures
            .filter((m) => m.seriesKey.trim())
            .slice(0, 4)
            .map((m) =>
              m.agg === 'count'
                ? { agg: 'count' as const, seriesKey: m.seriesKey.trim() }
                : { agg: m.agg, column: m.column, seriesKey: m.seriesKey.trim() },
            ),
          limit: Number.parseInt(wExploreLimit, 10) || 20,
        };
      }
      await api.post(`/institution/analytics/bi/dashboards/${selectedId}/widgets`, body);
      setWidgetDrawerOpen(false);
      void loadDashboardData(selectedId);
      void loadCatalogAndList();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.widgetAddError');
      setError(msg);
    }
  };

  const deleteWidget = async (widgetId: string) => {
    if (!selectedId) return;
    try {
      await api.delete(`/institution/analytics/bi/widgets/${widgetId}`);
      void loadDashboardData(selectedId);
      toast.success(t('institution.bi.toast.widgetRemoved'));
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : t('institution.bi.widgetDeleteError');
      setError(msg);
    }
  };

  const widgetIds = useMemo(() => dashboardData?.widgets.map((w) => w.id) ?? [], [dashboardData?.widgets]);

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-[#5B6472] dark:text-[#B6CAEC]">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  return (
    <div className="w-full max-w-none px-3 py-6 sm:px-4 lg:px-5">
      <header className="mb-6 space-y-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-[#0C1C38] dark:text-white">
            <LayoutGrid className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden />
            {t('institution.bi.title')}
          </h2>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.subtitle')}</p>
        </div>
        {(biGroups.length > 0 || biCategories.length > 0) && (
          <div className="flex flex-wrap items-start gap-3 rounded-lg border border-[#E8EDF5] bg-[#F8FAFC]/80 px-3 py-3 dark:border-[#B6CAEC] dark:bg-[#122644]/40">
            {biGroups.length > 0 ? (
              <div className="min-w-[10rem] flex-1">
                <InstitutionMultiSelect
                  id="inst-bi-filter-groups"
                  panelZIndexClass="z-[140]"
                  label={t('institution.bi.filterByGroup')}
                  options={biGroups.map((g) => ({ value: g.id, label: g.name }))}
                  value={filterGroupIds}
                  onChange={setFilterGroupIds}
                  emptySummaryLabel={t('institution.bi.filterAllGroupsSummary')}
                  formatMultipleSummary={(n) => t('institution.bi.filterSelectedMany', { count: n })}
                  placeholderSearch={t('institution.bi.filterSearchInList')}
                  noResultsLabel={t('institution.bi.filterListNoResults')}
                  formatRemoveChipAria={(name) => t('institution.bi.filterRemoveChipAria', { name })}
                />
              </div>
            ) : null}
            {biCategories.length > 0 ? (
              <div className="min-w-[10rem] flex-1">
                <InstitutionMultiSelect
                  id="inst-bi-filter-categories"
                  panelZIndexClass="z-[140]"
                  label={t('institution.bi.filterByCategory')}
                  options={biCategories.map((c) => ({ value: c.id, label: c.name }))}
                  value={filterCategoryIds}
                  onChange={setFilterCategoryIds}
                  emptySummaryLabel={t('institution.bi.filterAllCategoriesSummary')}
                  formatMultipleSummary={(n) => t('institution.bi.filterSelectedMany', { count: n })}
                  placeholderSearch={t('institution.bi.filterSearchInList')}
                  noResultsLabel={t('institution.bi.filterListNoResults')}
                  formatRemoveChipAria={(name) => t('institution.bi.filterRemoveChipAria', { name })}
                />
              </div>
            ) : null}
            <div className="flex shrink-0 flex-col gap-1 self-end">
              <span className="text-xs text-transparent select-none dark:text-transparent">.</span>
              <button
                type="button"
                onClick={() => {
                  setFilterGroupIds([]);
                  setFilterCategoryIds([]);
                }}
                className="rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm whitespace-nowrap dark:border-[#B6CAEC] dark:bg-[#122644]"
              >
                {t('institution.bi.filterAll')}
              </button>
            </div>
            <p className="w-full text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.multiSelectHint')}</p>
          </div>
        )}
        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            type="button"
            onClick={() => void loadCatalogAndList()}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm whitespace-nowrap dark:border-[#B6CAEC] dark:bg-[#122644]"
          >
            <RefreshCw className="h-4 w-4 shrink-0" />
            {t('institution.common.refresh')}
          </button>
          <div className="h-[38px] min-h-[38px] min-w-[12rem] max-w-full flex-1 sm:max-w-md sm:flex-initial">
            <InstitutionStyledSelect
              id="inst-bi-dashboard-select"
              label={false}
              panelZIndexClass="z-[140]"
              className="[&_button]:h-[38px] [&_button]:min-h-[38px] [&_button]:py-2"
              options={
                dashboardsFiltered.length === 0
                  ? [{ value: '', label: t('institution.bi.selectDashboardPlaceholder') }]
                  : dashboardsFiltered.map((d) => ({ value: d.id, label: d.title }))
              }
              value={dashboardSelectValue}
              onChange={(v) => {
                if (v) setSelectedId(v);
              }}
              disabled={dashboardsFiltered.length === 0}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setNewDashTitle('');
              setNewDashDesc('');
              setNewDashGroupIds([]);
              setNewDashCategoryIds([]);
              setAddOpen(true);
            }}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-2 text-sm font-medium whitespace-nowrap text-white"
          >
            <Plus className="h-4 w-4 shrink-0" />
            {t('institution.bi.newDashboard')}
          </button>
          {selectedId && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[#E8EDF5] bg-white p-2 text-[#5B6472] hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-[#B6CAEC] dark:bg-[#122644] dark:hover:bg-red-950/50"
              aria-label={t('institution.bi.deleteDashboard')}
              onClick={() => void deleteDashboard(selectedId)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {dashboards.length > 0 && dashboardsFiltered.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          {t('institution.bi.emptyFiltered')}
        </div>
      )}

      <section className="min-w-0 space-y-4">
        {dashboards.length === 0 && (
          <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.emptyList')}</p>
        )}

        {!selectedId && (
          <div className="rounded-xl border border-dashed border-[#E8EDF5] bg-[#F8FAFC]/50 px-4 py-12 text-center text-sm text-[#5B6472] sm:px-6 dark:border-[#B6CAEC] dark:bg-[#0C1C38]/30 dark:text-[#B6CAEC]">
            {t('institution.bi.pickDashboard')}
          </div>
        )}

        {selectedId && dashboardData && (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-[#0C1C38] dark:text-white">{dashboardData.dashboard.title}</h3>
                {dashboardData.dashboard.description && (
                  <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{dashboardData.dashboard.description}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const row = dashboards.find((d) => d.id === selectedId);
                    setRenameTitle(dashboardData.dashboard.title);
                    setEditDesc(row?.description ?? dashboardData.dashboard.description ?? '');
                    setEditGroupIds(row?.groupIds ?? dashboardData.dashboard.groupIds ?? []);
                    setEditCategoryIds(row?.categoryIds ?? dashboardData.dashboard.categoryIds ?? []);
                    setRenameOpen(true);
                  }}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm whitespace-nowrap dark:border-[#B6CAEC] dark:bg-[#122644]"
                >
                  <Pencil className="h-4 w-4 shrink-0" />
                  {t('institution.bi.editDashboard')}
                </button>
                <button
                  type="button"
                  onClick={() => void loadDashboardData(selectedId)}
                  disabled={dataLoading}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm whitespace-nowrap dark:border-[#B6CAEC] dark:bg-[#122644] disabled:opacity-50"
                >
                  {dataLoading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RefreshCw className="h-4 w-4 shrink-0" />}
                  {t('institution.bi.reloadData')}
                </button>
                <button
                  type="button"
                  onClick={openAddWidget}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-2 text-sm font-medium whitespace-nowrap text-white"
                >
                  <BarChart3 className="h-4 w-4 shrink-0" />
                  {t('institution.bi.addWidget')}
                </button>
              </div>
            </div>

            <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.hintDrag')}</p>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {dashboardData.widgets.map((w) => {
                      const ds = catalog?.datasets.find((d) => d.key === w.datasetKey);
                      return (
                        <SortableWidgetCard
                          key={w.id}
                          id={w.id}
                          className={cn((w.gridSpan ?? 1) >= 2 && 'xl:col-span-2')}
                        >
                          {({ attributes, listeners }) => (
                            <>
                              <div className="mb-3 flex items-start gap-2">
                                <button
                                  type="button"
                                  className="mt-0.5 cursor-grab touch-none rounded p-1 text-[#5B6472]/70 hover:bg-[#F0F4FA] dark:hover:bg-[#122644] active:cursor-grabbing"
                                  aria-label={t('institution.bi.hintDrag')}
                                  {...attributes}
                                  {...listeners}
                                >
                                  <GripVertical className="h-4 w-4" />
                                </button>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="font-medium text-[#0C1C38] dark:text-white">{w.title}</h4>
                                    <div className="flex shrink-0 gap-1">
                                      <button
                                        type="button"
                                        className="rounded p-1 text-[#5B6472] hover:bg-[#F0F4FA] dark:hover:bg-[#122644]"
                                        aria-label={t('institution.bi.editWidget')}
                                        onClick={() => openConfig(w)}
                                      >
                                        <Settings2 className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded p-1 text-[#5B6472]/70 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50"
                                        aria-label={t('institution.bi.deleteWidget')}
                                        onClick={() => void deleteWidget(w.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                                    {ds ? t(ds.labelKey) : w.datasetKey} · {t(`institution.bi.widgetType.${w.widgetType}`)}
                                  </p>
                                </div>
                              </div>
                              <BiWidgetChart widget={w} dateLocale={dateLocale} t={t} />
                            </>
                          )}
                        </SortableWidgetCard>
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>

              {dashboardData.widgets.length === 0 && (
                <div className="rounded-xl border border-dashed border-[#E8EDF5] px-6 py-10 text-center text-sm text-[#5B6472] dark:border-[#B6CAEC] dark:text-[#B6CAEC]">
                  {t('institution.bi.noWidgets')}
                </div>
              )}
            </>
          )}
      </section>

      {addOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true" aria-labelledby="inst-bi-new-dashboard-title">
            <button
              type="button"
              className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
              aria-label={t('institution.common.cancel')}
              onClick={() => setAddOpen(false)}
            />
            <div className="relative z-10 flex h-full w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
              <TrainerRightPanelHeader
                icon={Plus}
                title={t('institution.bi.newDashboard')}
                onClose={() => setAddOpen(false)}
                titleId="inst-bi-new-dashboard-title"
              />
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50/50 px-5 py-5 dark:bg-[#061128]/30 sm:px-6">
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.dashboardTitle')}
                  <input
                    className="mt-1 w-full rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                    value={newDashTitle}
                    onChange={(e) => setNewDashTitle(e.target.value)}
                    placeholder={t('institution.bi.defaultDashboardTitle')}
                  />
                </label>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.dashboardDescription')}
                  <textarea
                    className="mt-1 min-h-[4.5rem] w-full rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                    value={newDashDesc}
                    onChange={(e) => setNewDashDesc(e.target.value)}
                    rows={3}
                  />
                </label>
                <div>
                  <p className="text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.groupsVisibility')}</p>
                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.groupsVisibilityHint')}</p>
                  {biGroups.length === 0 ? (
                    <p className="mt-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noGroupsInInstitution')}</p>
                  ) : (
                    <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#122644]">
                      {biGroups.map((g) => (
                        <div key={g.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50">
                          <Switch
                            size="sm"
                            checked={newDashGroupIds.includes(g.id)}
                            onChange={(next) => {
                              setNewDashGroupIds((prev) =>
                                next ? (prev.includes(g.id) ? prev : [...prev, g.id]) : prev.filter((x) => x !== g.id),
                              );
                            }}
                          />
                          <span className="text-[#0C1C38] dark:text-[#DCE7F7]">{g.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.categoriesLabel')}</p>
                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.categoriesHint')}</p>
                  {biCategories.length === 0 ? (
                    <p className="mt-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noCategoriesInInstitution')}</p>
                  ) : (
                    <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#122644]">
                      {biCategories.map((c) => (
                        <div key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50">
                          <Switch
                            size="sm"
                            checked={newDashCategoryIds.includes(c.id)}
                            onChange={(next) => {
                              setNewDashCategoryIds((prev) =>
                                next ? (prev.includes(c.id) ? prev : [...prev, c.id]) : prev.filter((x) => x !== c.id),
                              );
                            }}
                          />
                          <span className="text-[#0C1C38] dark:text-[#DCE7F7]">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]"
                    onClick={() => setAddOpen(false)}
                  >
                    {t('institution.common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white"
                    onClick={() => void createDashboard()}
                  >
                    {t('institution.bi.create')}
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}

      {renameOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true" aria-labelledby="inst-bi-rename-dashboard-title">
            <button
              type="button"
              className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
              aria-label={t('institution.common.cancel')}
              onClick={() => setRenameOpen(false)}
            />
            <div className="relative z-10 flex h-full w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
              <TrainerRightPanelHeader
                icon={Pencil}
                title={t('institution.bi.editDashboard')}
                description={dashboardData?.dashboard.title}
                onClose={() => setRenameOpen(false)}
                titleId="inst-bi-rename-dashboard-title"
              />
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50/50 px-5 py-5 dark:bg-[#061128]/30 sm:px-6">
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.dashboardTitle')}
                  <input
                    className="mt-1 w-full rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                    value={renameTitle}
                    onChange={(e) => setRenameTitle(e.target.value)}
                  />
                </label>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.dashboardDescription')}
                  <textarea
                    className="mt-1 min-h-[4.5rem] w-full rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    rows={3}
                  />
                </label>
                <div>
                  <p className="text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.groupsVisibility')}</p>
                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.groupsVisibilityHint')}</p>
                  {biGroups.length === 0 ? (
                    <p className="mt-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noGroupsInInstitution')}</p>
                  ) : (
                    <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#122644]">
                      {biGroups.map((g) => (
                        <div key={g.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50">
                          <Switch
                            size="sm"
                            checked={editGroupIds.includes(g.id)}
                            onChange={(next) => {
                              setEditGroupIds((prev) =>
                                next ? (prev.includes(g.id) ? prev : [...prev, g.id]) : prev.filter((x) => x !== g.id),
                              );
                            }}
                          />
                          <span className="text-[#0C1C38] dark:text-[#DCE7F7]">{g.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.categoriesLabel')}</p>
                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.categoriesHint')}</p>
                  {biCategories.length === 0 ? (
                    <p className="mt-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noCategoriesInInstitution')}</p>
                  ) : (
                    <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#122644]">
                      {biCategories.map((c) => (
                        <div key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50">
                          <Switch
                            size="sm"
                            checked={editCategoryIds.includes(c.id)}
                            onChange={(next) => {
                              setEditCategoryIds((prev) =>
                                next ? (prev.includes(c.id) ? prev : [...prev, c.id]) : prev.filter((x) => x !== c.id),
                              );
                            }}
                          />
                          <span className="text-[#0C1C38] dark:text-[#DCE7F7]">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
                <div className="flex justify-end gap-2">
                  <button type="button" className="rounded-lg px-3 py-2 text-sm" onClick={() => setRenameOpen(false)}>
                    {t('institution.common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white"
                    onClick={() => void saveRename()}
                  >
                    {t('institution.common.save')}
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}

      {widgetDrawerOpen &&
        catalog &&
        wDataset === 'explore_chart' &&
        selectedDatasetMeta?.configHints?.exploreSchema &&
        createPortal(
          <div
            className="fixed inset-0 z-[10050] flex flex-col bg-white dark:bg-[#061128]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inst-bi-explore-fullscreen-title"
          >
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
              <div className="min-w-0">
                <h2
                  id="inst-bi-explore-fullscreen-title"
                  className="flex items-center gap-2 text-sm font-semibold text-[#0C1C38] dark:text-white"
                >
                  <BarChart3 className="h-5 w-5 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)]" aria-hidden />
                  <span className="truncate">{t('institution.bi.explore.fullscreenTitle')}</span>
                </h2>
                {selectedDatasetMeta.descriptionKey ? (
                  <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t(selectedDatasetMeta.descriptionKey)}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-xs text-[#5B6472]/70">{t('institution.bi.explore.escapeHint')}</span>
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm text-[#5B6472] hover:bg-[#F0F4FA] dark:text-[#DCE7F7] dark:hover:bg-[#122644]"
                  onClick={() => setWidgetDrawerOpen(false)}
                >
                  {t('institution.common.cancel')}
                </button>
              </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-hidden px-4 py-4 sm:px-6 sm:py-5">
                <div className="grid h-full min-h-0 grid-cols-1 gap-5 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)] xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
                  <div className="h-full min-h-0 overflow-y-auto pe-1">
                    <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 dark:border-[#19335E] dark:bg-[#0C1C38]/40">
                      <InstitutionStyledSelect
                        panelZIndexClass="z-[200]"
                        label={t('institution.bi.dataset')}
                        options={catalog.datasets.map((d) => ({ value: d.key, label: t(d.labelKey) }))}
                        value={wDataset}
                        onChange={(v) => {
                          setWDataset(v);
                          const meta = catalog?.datasets.find((d) => d.key === v);
                          if (v === 'explore_chart' && meta?.configHints?.exploreSchema?.[0]) {
                            const s0 = meta.configHints.exploreSchema[0];
                            setWExploreSource(s0.key);
                            setWDimension(s0.dimensions[0]?.key ?? 'name');
                            setWMeasures([{ agg: 'count', seriesKey: 'count' }]);
                            setWExploreLimit(String(meta.configHints.limitDefault ?? 20));
                          }
                          if (meta) setWTitle(t(meta.labelKey));
                        }}
                      />
                      <ExploreChartFormFields
                        t={t}
                        exploreSchema={selectedDatasetMeta.configHints.exploreSchema}
                        limitMax={selectedDatasetMeta.configHints.limitMax ?? 50}
                        source={wExploreSource}
                        setSource={setWExploreSource}
                        dimension={wDimension}
                        setDimension={setWDimension}
                        measures={wMeasures}
                        setMeasures={setWMeasures}
                        limitValue={wExploreLimit}
                        setLimitValue={setWExploreLimit}
                        widgetType={wType}
                      />
                      <InstitutionStyledSelect
                        panelZIndexClass="z-[200]"
                        label={t('institution.bi.visualization')}
                        options={(selectedDatasetMeta.allowedTypes ?? []).map((ty) => ({
                          value: ty,
                          label: t(`institution.bi.widgetType.${ty}`),
                        }))}
                        value={wType}
                        onChange={setWType}
                      />
                      <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                        {t('institution.bi.widgetTitle')}
                        <input
                          className="mt-1 w-full rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                          value={wTitle}
                          onChange={(e) => setWTitle(e.target.value)}
                        />
                      </label>
                      <InstitutionStyledSelect
                        panelZIndexClass="z-[200]"
                        label={t('institution.bi.gridWidth')}
                        options={[
                          { value: '1', label: t('institution.bi.gridHalf') },
                          { value: '2', label: t('institution.bi.gridFull') },
                        ]}
                        value={String(wGridSpan)}
                        onChange={(v) => setWGridSpan(Number(v))}
                      />
                    </div>
                  </div>

                  <div className="flex h-full min-h-[min(280px,45vh)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-[#19335E] dark:bg-[#0C1C38]/50 lg:min-h-0">
                    <div className="shrink-0">
                      <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white">
                        {t('institution.bi.explore.previewSectionTitle')}
                      </h3>
                      <p className="mt-0.5 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.explore.previewHint')}</p>
                    </div>
                    <div className="relative mt-3 min-h-0 flex-1">
                      {explorePreviewLoading ? (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 dark:bg-[#061128]/70">
                          <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#DCE7F7]">
                            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                            {t('institution.bi.explore.previewLoading')}
                          </div>
                        </div>
                      ) : null}
                      <div className="flex h-full min-h-[260px] flex-col">
                        {exploreAddPreviewWidget ? (
                          <BiWidgetChart
                            widget={exploreAddPreviewWidget}
                            dateLocale={dateLocale}
                            t={t}
                            fillHeight
                          />
                        ) : (
                          <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.noData')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]"
                    onClick={() => setWidgetDrawerOpen(false)}
                  >
                    {t('institution.common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white"
                    onClick={() => void addWidget()}
                  >
                    {t('institution.bi.add')}
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}

      {widgetDrawerOpen &&
        catalog &&
        wDataset !== 'explore_chart' &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true" aria-labelledby="inst-bi-add-widget-title">
            <button
              type="button"
              className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
              aria-label={t('institution.common.cancel')}
              onClick={() => setWidgetDrawerOpen(false)}
            />
            <div className="relative z-10 flex h-full w-full max-w-lg flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
              <TrainerRightPanelHeader
                icon={BarChart3}
                title={t('institution.bi.addWidget')}
                description={
                  selectedDatasetMeta?.descriptionKey ? t(selectedDatasetMeta.descriptionKey) : undefined
                }
                onClose={() => setWidgetDrawerOpen(false)}
                titleId="inst-bi-add-widget-title"
              />
              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50 px-5 py-5 dark:bg-[#061128]/30 sm:px-6">
                <div className="space-y-4">
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.bi.dataset')}
                    options={catalog.datasets.map((d) => ({ value: d.key, label: t(d.labelKey) }))}
                    value={wDataset}
                    onChange={(v) => {
                      setWDataset(v);
                      const meta = catalog?.datasets.find((d) => d.key === v);
                      if (v === 'explore_chart' && meta?.configHints?.exploreSchema?.[0]) {
                        const s0 = meta.configHints.exploreSchema[0];
                        setWExploreSource(s0.key);
                        setWDimension(s0.dimensions[0]?.key ?? 'name');
                        setWMeasures([{ agg: 'count', seriesKey: 'count' }]);
                        setWExploreLimit(String(meta.configHints.limitDefault ?? 20));
                      }
                      if (meta) setWTitle(t(meta.labelKey));
                    }}
                  />
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.bi.visualization')}
                    options={(selectedDatasetMeta?.allowedTypes ?? []).map((ty) => ({
                      value: ty,
                      label: t(`institution.bi.widgetType.${ty}`),
                    }))}
                    value={wType}
                    onChange={setWType}
                  />
                  <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7]">
                    {t('institution.bi.widgetTitle')}
                    <input
                      className="mt-1 w-full rounded-lg border border-[#E8EDF5] px-3 py-2 text-sm dark:border-[#B6CAEC] dark:bg-[#122644]"
                      value={wTitle}
                      onChange={(e) => setWTitle(e.target.value)}
                    />
                  </label>
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.bi.gridWidth')}
                    options={[
                      { value: '1', label: t('institution.bi.gridHalf') },
                      { value: '2', label: t('institution.bi.gridFull') },
                    ]}
                    value={String(wGridSpan)}
                    onChange={(v) => setWGridSpan(Number(v))}
                  />
                </div>
              </div>
              <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm"
                    onClick={() => setWidgetDrawerOpen(false)}
                  >
                    {t('institution.common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white"
                    onClick={() => void addWidget()}
                  >
                    {t('institution.bi.add')}
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}

      {configOpen &&
        configWidget &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true" aria-labelledby="inst-bi-config-widget-title">
            <button
              type="button"
              className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
              aria-label={t('institution.common.cancel')}
              onClick={() => !cfgSaving && setConfigOpen(false)}
            />
            <div className="relative z-10 flex h-full w-full max-w-lg flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
              <TrainerRightPanelHeader
                icon={Settings2}
                title={t('institution.bi.configAdvanced')}
                description={configWidget.title}
                onClose={() => !cfgSaving && setConfigOpen(false)}
                closeDisabled={cfgSaving}
                titleId="inst-bi-config-widget-title"
              />
              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50 px-5 py-5 dark:bg-[#061128]/30 sm:px-6">
            <div className="space-y-4 text-sm">
              <InstitutionStyledSelect
                panelZIndexClass="z-[200]"
                label={t('institution.bi.gridWidth')}
                options={[
                  { value: '1', label: t('institution.bi.gridHalf') },
                  { value: '2', label: t('institution.bi.gridFull') },
                ]}
                value={String(cfgGridSpan)}
                onChange={(v) => setCfgGridSpan(Number(v))}
              />

              {configHints?.exploreSchema && configWidget.datasetKey === 'explore_chart' && (
                <div className="space-y-3 rounded-lg border border-[#E8EDF5] bg-[#F8FAFC]/80 p-3 dark:border-[#B6CAEC] dark:bg-[#122644]/40">
                  <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.bi.explore.maxMeasures')}</p>
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.bi.explore.dataSource')}
                    options={configHints.exploreSchema.map((s) => ({ value: s.key, label: t(s.labelKey) }))}
                    value={cfgExploreSource}
                    onChange={(src) => {
                      setCfgExploreSource(src);
                      const sch = configHints.exploreSchema?.find((s) => s.key === src);
                      if (sch?.dimensions[0]) setCfgDimension(sch.dimensions[0].key);
                      setCfgMeasures([{ agg: 'count', seriesKey: 'count' }]);
                    }}
                  />
                  {(() => {
                    const srcDef =
                      configHints.exploreSchema?.find((s) => s.key === cfgExploreSource) ??
                      configHints.exploreSchema?.[0];
                    if (!srcDef) return null;
                    return (
                      <>
                        <InstitutionStyledSelect
                          panelZIndexClass="z-[200]"
                          label={t('institution.bi.explore.dimension')}
                          options={srcDef.dimensions.map((d) => ({
                            value: d.key,
                            label: t(d.labelKey) !== d.labelKey ? t(d.labelKey) : d.key,
                          }))}
                          value={cfgDimension}
                          onChange={setCfgDimension}
                        />
                        <fieldset>
                          <legend className="font-medium text-[#121826] dark:text-[#DCE7F7]">
                            {t('institution.bi.explore.measures')}
                          </legend>
                          <div className="mt-2 space-y-2">
                            {cfgMeasures.map((row, idx) => (
                              <div
                                key={idx}
                                className="flex flex-col gap-2 rounded-md border border-[#E8EDF5] bg-white p-2 dark:border-[#B6CAEC] dark:bg-[#0C1C38]"
                              >
                                <div className="flex flex-wrap items-end gap-2">
                                  <div className="min-w-[120px] flex-1">
                                    <InstitutionStyledSelect
                                      panelZIndexClass="z-[200]"
                                      label={t('institution.bi.explore.measureAgg')}
                                      className="[&_button]:px-2 [&_button]:py-1 [&_button]:text-sm"
                                      options={[
                                        { value: 'count', label: t('institution.bi.explore.agg.count') },
                                        {
                                          value: 'sum',
                                          label: t('institution.bi.explore.agg.sum'),
                                          disabled: !srcDef.numericColumns.length,
                                        },
                                        {
                                          value: 'avg',
                                          label: t('institution.bi.explore.agg.avg'),
                                          disabled: !srcDef.numericColumns.length,
                                        },
                                      ]}
                                      value={row.agg}
                                      onChange={(v) => {
                                        const agg = v as ExploreMeasureForm['agg'];
                                        const firstNum = srcDef.numericColumns[0]?.key;
                                        setCfgMeasures((prev) =>
                                          prev.map((m, i) =>
                                            i === idx
                                              ? {
                                                  agg,
                                                  column: agg === 'count' ? undefined : firstNum,
                                                  seriesKey: suggestExploreSeriesKey(agg, agg === 'count' ? undefined : firstNum),
                                                }
                                              : m,
                                          ),
                                        );
                                      }}
                                    />
                                  </div>
                                  {row.agg !== 'count' && (
                                    <div className="min-w-[140px] flex-1">
                                      <InstitutionStyledSelect
                                        panelZIndexClass="z-[200]"
                                        label={t('institution.bi.explore.columnForAgg')}
                                        className="[&_button]:px-2 [&_button]:py-1 [&_button]:text-sm"
                                        options={srcDef.numericColumns.map((nc) => ({
                                          value: nc.key,
                                          label: t(nc.labelKey) !== nc.labelKey ? t(nc.labelKey) : nc.key,
                                        }))}
                                        value={row.column ?? ''}
                                        onChange={(col) => {
                                          setCfgMeasures((prev) =>
                                            prev.map((m, i) =>
                                              i === idx
                                                ? {
                                                    ...m,
                                                    column: col,
                                                    seriesKey: suggestExploreSeriesKey(m.agg, col),
                                                  }
                                                : m,
                                            ),
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  <label className="min-w-[100px] flex-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                                    {t('institution.bi.explore.seriesKey')}
                                    <input
                                      className="mt-0.5 w-full rounded border border-[#E8EDF5] px-2 py-1 font-mono text-xs dark:border-[#B6CAEC] dark:bg-[#122644]"
                                      value={row.seriesKey}
                                      onChange={(e) =>
                                        setCfgMeasures((prev) =>
                                          prev.map((m, i) => (i === idx ? { ...m, seriesKey: e.target.value } : m)),
                                        )
                                      }
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="text-xs text-red-600 hover:underline dark:text-red-400"
                                    onClick={() => setCfgMeasures((prev) => prev.filter((_, i) => i !== idx))}
                                    disabled={cfgMeasures.length <= 1}
                                  >
                                    {t('institution.bi.explore.removeMeasure')}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="mt-2 text-sm text-[color:var(--inst-player-primary,#0C1C38)] hover:underline"
                            onClick={() =>
                              setCfgMeasures((prev) =>
                                prev.length >= 4
                                  ? prev
                                  : [...prev, { agg: 'count', seriesKey: `m${prev.length + 1}` }],
                              )
                            }
                            disabled={cfgMeasures.length >= 4}
                          >
                            {t('institution.bi.explore.addMeasure')}
                          </button>
                        </fieldset>
                        {configWidget.widgetType === 'pie' && (
                          <p className="text-xs text-amber-800 dark:text-amber-200">{t('institution.bi.explore.pieNote')}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {configHints?.supportsLimit && (
                <label className="block font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.limit')}
                  <input
                    type="number"
                    min={1}
                    max={configHints.limitMax ?? 9999}
                    className="mt-1 w-full rounded-lg border border-[#E8EDF5] px-3 py-2 dark:border-[#B6CAEC] dark:bg-[#122644]"
                    value={cfgLimit}
                    onChange={(e) => setCfgLimit(e.target.value)}
                  />
                </label>
              )}

              {configHints?.kpiKeys && configHints.kpiKeys.length > 0 && (
                <fieldset>
                  <legend className="font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.kpiPick')}</legend>
                  <div className="mt-2 flex max-h-40 flex-col gap-2 overflow-y-auto">
                    {configHints.kpiKeys.map((k) => (
                      <div key={k.key} className="flex items-center gap-2">
                        <Switch
                          size="sm"
                          checked={cfgKpis.includes(k.key)}
                          onChange={(next) => {
                            if (next) setCfgKpis((p) => (p.includes(k.key) ? p : [...p, k.key]));
                            else setCfgKpis((p) => p.filter((x) => x !== k.key));
                          }}
                        />
                        <span>{t(k.labelKey)}</span>
                      </div>
                    ))}
                  </div>
                </fieldset>
              )}

              {configHints?.tableColumns && configHints.tableColumns.length > 0 && (
                <fieldset>
                  <legend className="font-medium text-[#121826] dark:text-[#DCE7F7]">{t('institution.bi.columnsPick')}</legend>
                  <div className="mt-2 flex max-h-40 flex-col gap-2 overflow-y-auto">
                    {configHints.tableColumns.map((c) => (
                      <div key={c.key} className="flex items-center gap-2">
                        <Switch
                          size="sm"
                          checked={cfgCols.includes(c.key)}
                          onChange={(next) => {
                            if (next) setCfgCols((p) => (p.includes(c.key) ? p : [...p, c.key]));
                            else setCfgCols((p) => p.filter((x) => x !== c.key));
                          }}
                        />
                        <span>{t(c.labelKey)}</span>
                      </div>
                    ))}
                  </div>
                </fieldset>
              )}

              {configHints?.barMetricKeys &&
                configHints.barMetricKeys.length > 0 &&
                configWidget.widgetType === 'bar' &&
                (configWidget.datasetKey === 'top_flows' || configWidget.datasetKey === 'flows_overview') && (
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.bi.barMetric')}
                    options={configHints.barMetricKeys.map((m) => ({ value: m.key, label: t(m.labelKey) }))}
                    value={cfgYKey}
                    onChange={setCfgYKey}
                  />
                )}

              {configHints?.supportsColors && (
                <label className="block font-medium text-[#121826] dark:text-[#DCE7F7]">
                  {t('institution.bi.seriesColors')}
                  <textarea
                    className="mt-1 w-full rounded-lg border border-[#E8EDF5] px-3 py-2 font-mono text-xs dark:border-[#B6CAEC] dark:bg-[#122644]"
                    rows={4}
                    placeholder={t('institution.bi.colorsHint')}
                    value={cfgColors}
                    onChange={(e) => setCfgColors(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-[#5B6472]">{t('institution.bi.colorsHint')}</p>
                </label>
              )}
            </div>
              </div>
              <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38] sm:px-6">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-2 text-sm"
                    disabled={cfgSaving}
                    onClick={() => setConfigOpen(false)}
                  >
                    {t('institution.common.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={cfgSaving}
                    className="rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    onClick={() => void saveWidgetConfig()}
                  >
                    {cfgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('institution.bi.saveConfig')}
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
